import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import styled from 'styled-components';
import {
    RebelButton,
    RebelSecondaryButton,
    RebelLabel,
    RebelInput,
    RebelTextarea,
    RebelSelect
} from '../../../styles/rebel-experiences';
import { GetDB, generateGUID } from '../../../utils/database/firebase-budget-tool';
import { setDocument } from '../../../utils/firestore-functions';

const AgendaActivityForm = ({ locationId, activity, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        activity_id: '',
        activity_name: '',
        category: '',
        sub_category: '',
        description: '',
        price_per_person: 0,
        flat_price: 0,
        duration_hours: 0,
        min_time: 0,
        max_time: 0,
        max_participants: 0,
        min_participants: 0,
        image_url: '',
        is_active: true,
        location_id: locationId
    });
    const [saving, setSaving] = useState(false);

    const budgetDB = GetDB();

    useEffect(() => {
        if (activity) {
            setFormData(activity);
        }
    }, [activity]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Generate activity_id as GUID for new activities
    useEffect(() => {
        if (!activity && !formData.activity_id) {
            setFormData(prev => ({
                ...prev,
                activity_id: generateGUID()
            }));
        }
    }, [activity, formData.activity_id]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const activityId = activity?.id || generateGUID();
            const dataToSave = {
                ...formData,
                id: activityId,
                location_id: locationId,
                updated_at: new Date(),
                // Convert string inputs to numbers
                price_per_person: parseFloat(formData.price_per_person) || 0,
                flat_price: parseFloat(formData.flat_price) || 0,
                duration_hours: parseFloat(formData.duration_hours) || 0,
                min_time: parseFloat(formData.min_time) || 0,
                max_time: parseFloat(formData.max_time) || 0,
                max_participants: parseInt(formData.max_participants) || 0,
                min_participants: parseInt(formData.min_participants) || 0
            };

            if (!activity) {
                dataToSave.created_at = new Date();
            }

            // Wait for the save to complete before closing the modal
            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-config-activities",
                    activityId,
                    dataToSave,
                    () => resolve(),  // success callback
                    (error) => reject(error)  // failure callback
                );
            });

            onSave();
        } catch (error) {
            console.error('Error saving activity:', error);
            alert('Error saving activity. Please try again.');
            setSaving(false);
        }
    };

    const getSubCategoryOptions = () => {
        const subCategories = {
            'meals': ['Breakfast', 'Lunch', 'Dinner', 'Snack'],
            'morning-activity': ['Adventure', 'Wellness', 'Cultural', 'Nature', 'Sports'],
            'evening-activities': ['Entertainment', 'Social', 'Cultural', 'Recreation'],
            'business': ['Workshop', 'Presentation', 'Meeting', 'Conference', 'Planning'],
            'free-time': ['Relaxation', 'Arrival', 'Departure'],
            'team-building': ['Collaboration', 'Communication', 'Leadership', 'Problem-Solving'],
            'learning': ['Workshop', 'Training', 'Seminar', 'Educational', 'Speaker']
        };
        return subCategories[formData.category] || [];
    };

    return (
        <FormContainer>
            <FormGroup>
                <RebelLabel>Activity Name *</RebelLabel>
                <RebelInput
                    type="text"
                    value={formData.activity_name}
                    onChange={(e) => handleChange('activity_name', e.target.value)}
                    placeholder="e.g., Traditional Costa Rican Breakfast"
                />
                {formData.activity_id && (
                    <SmallText>ID: {formData.activity_id}</SmallText>
                )}
            </FormGroup>

            <SectionTitle>Category, Pricing, Duration & Participants</SectionTitle>

            <Row>
                <Col md={3}>
                    <ColumnLabel>Category</ColumnLabel>
                    <FormGroup>
                        <RebelLabel>Category *</RebelLabel>
                        <RebelSelect
                            value={formData.category}
                            onChange={(e) => {
                                handleChange('category', e.target.value);
                                handleChange('sub_category', ''); // Reset sub-category when category changes
                            }}
                        >
                            <option value="">Select Category</option>
                            <option value="meals">Meals</option>
                            <option value="morning-activity">Morning Activity</option>
                            <option value="evening-activities">Evening Activities</option>
                            <option value="business">Business</option>
                            <option value="free-time">Free Time</option>
                            <option value="team-building">Team Building</option>
                            <option value="learning">Learning</option>
                        </RebelSelect>
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Sub-Category</RebelLabel>
                        <RebelSelect
                            value={formData.sub_category}
                            onChange={(e) => handleChange('sub_category', e.target.value)}
                            disabled={!formData.category}
                        >
                            <option value="">Select Sub-Category</option>
                            {getSubCategoryOptions().map(sub => (
                                <option key={sub} value={sub.toLowerCase()}>{sub}</option>
                            ))}
                        </RebelSelect>
                        {!formData.category && <SmallText>Select a category first</SmallText>}
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <ColumnLabel>Pricing</ColumnLabel>
                    <FormGroup>
                        <RebelLabel>Price per Person ($)</RebelLabel>
                        <RebelInput
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.price_per_person}
                            onChange={(e) => handleChange('price_per_person', e.target.value)}
                            placeholder="0"
                        />
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Flat Price ($)</RebelLabel>
                        <RebelInput
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.flat_price}
                            onChange={(e) => handleChange('flat_price', e.target.value)}
                            placeholder="0"
                        />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <ColumnLabel>Duration & Timing</ColumnLabel>
                    <FormGroup>
                        <RebelLabel>Duration (hours)</RebelLabel>
                        <RebelInput
                            type="number"
                            min="0"
                            step="0.5"
                            value={formData.duration_hours}
                            onChange={(e) => handleChange('duration_hours', e.target.value)}
                            placeholder="0"
                        />
                    </FormGroup>
                    <Row>
                        <Col xs={6}>
                            <FormGroup>
                                <RebelLabel>Min (hours)</RebelLabel>
                                <RebelInput
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={formData.min_time}
                                    onChange={(e) => handleChange('min_time', e.target.value)}
                                    placeholder="0"
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <RebelLabel>Max (hours)</RebelLabel>
                                <RebelInput
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={formData.max_time}
                                    onChange={(e) => handleChange('max_time', e.target.value)}
                                    placeholder="0"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
                <Col md={3}>
                    <ColumnLabel>Participants</ColumnLabel>
                    <FormGroup>
                        <RebelLabel>Min Participants</RebelLabel>
                        <RebelInput
                            type="number"
                            min="0"
                            value={formData.min_participants}
                            onChange={(e) => handleChange('min_participants', e.target.value)}
                            placeholder="1"
                        />
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Max Participants</RebelLabel>
                        <RebelInput
                            type="number"
                            min="0"
                            value={formData.max_participants}
                            onChange={(e) => handleChange('max_participants', e.target.value)}
                            placeholder="20"
                        />
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <RebelLabel>Description</RebelLabel>
                <WideTextarea
                    rows="2"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Detailed description of the activity..."
                />
            </FormGroup>

            <SectionTitle>Additional Details</SectionTitle>

            <FormGroup>
                <RebelLabel>Image URL</RebelLabel>
                <RebelInput
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="https://cdn.moniker-virtual.com/agenda/activity.jpg"
                />
                {formData.image_url && (
                    <ImagePreview>
                        <img src={formData.image_url} alt="Activity preview" />
                    </ImagePreview>
                )}
            </FormGroup>

            <FormGroup>
                <ToggleContainer>
                    <RebelLabel>Status</RebelLabel>
                    <ToggleSwitch>
                        <ToggleInput
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => handleChange('is_active', e.target.checked)}
                        />
                        <ToggleSlider />
                    </ToggleSwitch>
                    <ToggleLabel>{formData.is_active ? 'Active' : 'Inactive'}</ToggleLabel>
                </ToggleContainer>
            </FormGroup>

            <ButtonContainer>
                <RebelSecondaryButton onClick={onCancel} disabled={saving}>
                    Cancel
                </RebelSecondaryButton>
                <RebelButton
                    onClick={handleSave}
                    disabled={saving || !formData.activity_name || !formData.category}
                >
                    {saving ? 'Saving...' : activity ? 'Update Activity' : 'Create Activity'}
                </RebelButton>
            </ButtonContainer>
        </FormContainer>
    );
};

export default AgendaActivityForm;

const FormContainer = styled.div`
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px;
    margin: -24px;
    margin-bottom: 0;
`;

const SectionTitle = styled.h6`
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 16px;
    margin-top: 24px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(102, 126, 234, 0.3);

    &:first-of-type {
        margin-top: 0;
    }
`;

const ColumnLabel = styled.div`
    color: #667eea;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ImagePreview = styled.div`
    margin-top: 12px;

    img {
        max-width: 100%;
        max-height: 200px;
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.1);
    }
`;

const SmallText = styled.small`
    display: block;
    margin-top: 4px;
    color: #888;
    font-size: 12px;
`;

const WideTextarea = styled(RebelTextarea)`
    width: 100%;
    min-height: 60px;
    max-height: 120px;
    resize: vertical;
    font-size: 14px;
    line-height: 1.6;
`;

const ToggleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;

    /* Ensure Status label is vertically centered */
    > label {
        margin-bottom: 0;
        line-height: 1;
    }
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    height: 26px;
`;

const ToggleInput = styled.input`
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
        background: linear-gradient(135deg, #4a9eff 0%, #667eea 100%);
    }

    &:checked + span:before {
        transform: translateX(24px);
    }
`;

const ToggleSlider = styled.span`
    position: relative;
    display: inline-block;
    width: 50px;
    height: 26px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 34px;
    transition: 0.3s;

    &:before {
        content: "";
        position: absolute;
        height: 20px;
        width: 20px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        border-radius: 50%;
        transition: 0.3s;
    }
`;

const ToggleLabel = styled.span`
    color: #ffffff;
    font-size: 14px;
    line-height: 1;
    display: inline-block;
`;

// Override reactstrap FormGroup to ensure labels are always above inputs
const FormGroup = styled.div`
    margin-bottom: 1rem;

    label {
        display: block;
        margin-bottom: 0.5rem;
    }

    /* Exception for toggle containers - labels should be inline */
    ${ToggleContainer} label {
        display: inline;
        margin-bottom: 0;
    }

    /* Custom select styling */
    select {
        height: 48px;
        padding: 13px 16px;
        line-height: 1.4;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        background-size: 12px;
        padding-right: 40px;
        width: 100%;

        /* Ensure option text isn't cut off */
        option {
            padding: 10px 12px;
            background: #2d2d2d;
            color: #ffffff;
            line-height: 1.6;
        }
    }

    /* Match input height */
    input[type="text"],
    input[type="number"],
    input[type="email"],
    input[type="password"] {
        height: 46px;
        width: 100%;
    }
`;
